import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useReloadAfterDataFlush } from 'hooks/useReloadAfterDataFlush';
import { isDocumentationPath } from 'setup/navigation/paths';
import { isStandard } from 'shared/domain/role/isStandard';
import { useProjectActions, useProjectId } from 'store/project';
import { useCurrentUserRole } from 'store/user';

/**
 * Binds the project data loading of `store/project` to the selected project,
 * the role (a standard user has no inspections) and the worker's data
 * flushes. Mounted once for the signed-in user, above the project selection.
 */
export function useProjectDataLoadingInitializer(): void {
  const { pathname } = useLocation();
  const projectId = useProjectId();
  const waitsForInspectionData = !isStandard(useCurrentUserRole());
  const {
    markAllDataPending,
    setWaitsForInspectionData,
    resetDataLoading,
    startProjectDataLoading,
    stopProjectDataLoading,
    reloadProjectDataAfterFlush,
  } = useProjectActions();

  useEffect(() => (): void => resetDataLoading(), [resetDataLoading]);

  useEffect(() => {
    if (projectId) {
      markAllDataPending();
    }
  }, [projectId, markAllDataPending]);

  useEffect(() => {
    setWaitsForInspectionData(waitsForInspectionData);
    if (!projectId) {
      return undefined;
    }
    startProjectDataLoading();
    return stopProjectDataLoading;
  }, [projectId, waitsForInspectionData, setWaitsForInspectionData, startProjectDataLoading, stopProjectDataLoading]);

  // The documentation view reloads its own data after a flush and stays open.
  const reloadAfterFlush = useCallback(() => {
    if (!isDocumentationPath(pathname)) {
      reloadProjectDataAfterFlush();
    }
  }, [pathname, reloadProjectDataAfterFlush]);
  useReloadAfterDataFlush(reloadAfterFlush);
}
